import { useState, useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import SkeletonLoadWrapper from 'pricing-pages-ui-components/loading/SkeletonLoadWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
// Deduplicates concurrent register() calls across hook instances that share the
// same translations object reference (e.g. multiple components importing the
// same i2l? lang file). WeakMap keeps memory bounded — entries are collected
// when the translations object is no longer referenced anywhere.
const registrationCache = new WeakMap();

// Defined at module scope so its component identity is stable across renders.
// Re-creating it inside the hook would unmount/remount the children subtree
// every render, causing flicker and dropped focus.
const TranslationSkeleton = ({
  isLoading,
  children,
  widths,
  heights
}) => /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
  isLoading: isLoading,
  widths: widths,
  heights: heights,
  children: children
});
export const useScopedTranslations = (translations, i18nLoader, shouldLoad = true) => {
  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const hasRegistered = useRef(false);
  useEffect(() => {
    if (!shouldLoad || !i18nLoader || hasRegistered.current) return;
    hasRegistered.current = true;
    let cancelled = false;
    const isObject = typeof translations === 'object' && translations !== null;
    const cacheKey = translations;

    // Reuse an in-flight or settled promise for the same translations reference
    // so concurrent hook instances don't each call register() independently.
    const cached = isObject ? registrationCache.get(cacheKey) : undefined;
    const promise = cached !== null && cached !== void 0 ? cached : i18nLoader.register(translations);

    // Cache the new promise and evict on failure so remounts can retry.
    if (isObject && !cached) {
      registrationCache.set(cacheKey, promise);
      promise.catch(() => {
        if (registrationCache.get(cacheKey) === promise) {
          registrationCache.delete(cacheKey);
        }
      });
    }
    promise.then(() => {
      if (!cancelled) setIsReady(true);
    }).catch(e => {
      if (!cancelled) {
        setIsError(true);
        Raven.captureException(e, {
          fingerprint: ['Failed to register translations']
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [i18nLoader, shouldLoad, translations]);

  // On error, render children with a static fallback so the page is not blank.
  const isLoading = !(isReady || isError);

  // useMemo so the bound component reference only changes when isLoading flips,
  // not on every render of the consumer.
  const Skeleton = useMemo(() => function BoundSkeleton(props) {
    return /*#__PURE__*/_jsx(TranslationSkeleton, Object.assign({
      isLoading: isLoading
    }, props));
  }, [isLoading]);
  return {
    isReady,
    isError,
    Skeleton
  };
};